import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye, EyeSlash, ArrowsLeftRight, Plus, Phone, CreditCard as CardIcon,
  CurrencyInr, ArrowUpRight, ArrowDownLeft, X, Buildings, MapPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { passengerApi, getPassengerUser } from "@/lib/passengerApi";
import { MajheHeader } from "@/components/majhe/MajheLayout";
import DocumentUploader from "@/components/majhe/DocumentUploader";
import { useMajheMeta } from "@/lib/useMajheMeta";

export default function CardHub() {
  const navigate = useNavigate();
  const user = getPassengerUser();
  const [cards, setCards] = useState([]);
  const [active, setActive] = useState(null);
  const [showBalance, setShowBalance] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await passengerApi.get("/me/cards");
      setCards(data);
      if (data[0] && !active) setActive(data[0]);
      else if (active) setActive(data.find((c) => c.id === active.id) || data[0] || null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="majhe-card-hub">
      <MajheHeader title="Card Details & Recharge" onBack={() => navigate("/majhe")}/>

      {loading && <div className="px-5"><div className="majhe-skel h-44"/></div>}

      {!loading && cards.length === 0 && (
        <div className="px-5 mt-2">
          <div className="majhe-card text-center py-8">
            <CardIcon size={42} className="mx-auto mb-2"/>
            <div className="font-extrabold text-lg">No Transit Card yet</div>
            <div className="text-sm opacity-85 mt-1">Apply for a Smart Transit Card or link your existing physical card</div>
          </div>
          <div className="space-y-2.5 mt-4">
            <button onClick={() => setShowRecharge("apply")} data-testid="card-apply-btn" className="majhe-btn">
              <Plus size={18} weight="bold"/> Apply for new Smart Transit Card
            </button>
            <button onClick={() => setShowLink(true)} data-testid="card-link-btn" className="majhe-btn majhe-btn--ghost">
              Link existing physical card
            </button>
          </div>
        </div>
      )}

      {!loading && active && (
        <div className="px-5 mt-1">
          {/* Hero Card */}
          <div data-testid="card-hero" className="majhe-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[18px] font-extrabold tracking-wide uppercase">
                  {(active.holder_name || "Passenger").toUpperCase()}
                </div>
                <div className="font-mono tracking-wider mt-1 opacity-90">{active.card_no}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5 mt-4">
              <HiddenAmount label="Current Balance" value={`₹${Number(active.balance || 0).toFixed(0)}`}
                shown={showBalance} tid="balance"/>
              <HiddenAmount label="Pending Top-Ups" value={"₹0"} shown={showBalance} tid="pending"/>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <button onClick={() => setShowBalance((v) => !v)} data-testid="balance-toggle"
                type="button"
                aria-label={showBalance ? "Hide card balance details" : "Show card balance details"}
                aria-pressed={showBalance}
                className="text-[11px] font-bold uppercase tracking-wider opacity-80 flex items-center gap-1">
                {showBalance ? <EyeSlash size={14} aria-hidden="true"/> : <Eye size={14} aria-hidden="true"/>}
                {showBalance ? "Hide details" : "Show details"}
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button data-testid="card-recharge-btn" onClick={() => setShowRecharge(active.card_no)}
                type="button" aria-label={`Recharge card ${active.card_no}`}
                className="majhe-card-btn"><Plus size={12} weight="bold" aria-hidden="true"/> Recharge</button>
              <button data-testid="card-history-btn" onClick={() => setShowHistory(true)}
                type="button" aria-label="Show transaction history"
                className="majhe-card-btn"><ArrowsLeftRight size={12} aria-hidden="true"/> History</button>
            </div>
          </div>

          {/* Other cards (if multiple) */}
          {cards.length > 1 && (
            <div className="mt-4">
              <h3 className="text-[12px] uppercase tracking-wider text-[#7B7390] font-bold mb-2 px-1">Your linked cards ({cards.length})</h3>
              <div className="space-y-2">
                {cards.map((c) => (
                  <button key={c.id} onClick={() => setActive(c)} data-testid={`card-pick-${c.card_no}`}
                    className={`w-full text-left bg-white border rounded-xl p-3 flex items-center gap-3 ${active.id === c.id ? "border-[#5B2EE5]" : "border-[#EDE8FA]"}`}>
                    <div className="w-10 h-10 rounded-lg bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center">
                      <CardIcon size={18} weight="fill"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[12px] text-[#18142A] truncate">{c.card_no}</div>
                      <div className="text-[10.5px] text-[#7B7390]">{c.holder_name}</div>
                    </div>
                    <div className="text-[12px] font-extrabold text-[#5B2EE5]">₹{Number(c.balance || 0).toFixed(0)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 space-y-2.5">
            <button onClick={() => setShowRecharge("new")} data-testid="card-add-btn" className="majhe-btn">
              <Plus size={18} weight="bold"/> Recharge New Card
            </button>
            <button onClick={() => setShowLink(true)} data-testid="card-link-existing-btn" className="majhe-btn majhe-btn--ghost">
              Link another physical card
            </button>
            <button onClick={() => setShowRecharge("apply")} data-testid="card-apply2-btn" className="majhe-btn majhe-btn--ghost">
              Apply for a brand new card
            </button>
          </div>
        </div>
      )}

      {showHistory && active && <HistorySheet card={active} onClose={() => setShowHistory(false)}/>}
      {showRecharge === "apply" && (
        <ApplyCardSheet user={user}
          onClose={() => setShowRecharge(false)}
          onDone={(meta) => {
            setShowRecharge(false);
            if (meta?.navigateTo) { navigate(meta.navigateTo); return; }
            load();
          }}/>
      )}
      {showRecharge && showRecharge !== "apply" && (
        <RechargeSheet mode={showRecharge} activeCard={active} user={user}
          onClose={() => setShowRecharge(false)}
          onDone={() => { setShowRecharge(false); load(); }}/>
      )}
      {showLink && <LinkCardSheet onClose={() => setShowLink(false)}
        onDone={() => { setShowLink(false); load(); }}/>}
    </div>
  );
}

function HiddenAmount({ label, value, shown, tid }) {
  return (
    <div className="majhe-hidden-amt" data-testid={`hidden-${tid}`}>
      <div className="flex-1">
        <div className="majhe-hidden-amt__label">{label}</div>
        <div className="majhe-hidden-amt__value">
          {shown ? value : "* * * * * *"}
        </div>
      </div>
      <div className="opacity-75">{shown ? <Eye size={16}/> : <EyeSlash size={16}/>}</div>
    </div>
  );
}

function HistorySheet({ card, onClose }) {
  const [history, setHistory] = useState(card.history || []);
  useEffect(() => {
    passengerApi.get(`/cards/${encodeURIComponent(card.card_no)}/history`)
      .then(({ data }) => setHistory(data.history || []));
  }, [card.card_no]);

  return (
    <Sheet title="Transaction History" onClose={onClose} tid="card-history-sheet">
      <div className="majhe-card !mb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="font-extrabold uppercase tracking-wider">{card.holder_name}</div>
          <div className="font-mono opacity-90">{card.card_no.slice(0, 16)}</div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <HiddenAmount label="Current Balance" value={`₹${Number(card.balance || 0).toFixed(0)}`} shown tid="hist-bal"/>
          <HiddenAmount label="Pending Top-Ups" value={"₹0"} shown tid="hist-pend"/>
        </div>
      </div>
      <h3 className="text-[20px] font-extrabold text-[#18142A] mb-3">Transaction History</h3>
      {history.length === 0 && <div className="text-center py-8 text-[#7B7390]">No transactions yet</div>}
      {history.map((h, i) => {
        const isCredit = h.kind?.toLowerCase().includes("top up") || h.kind?.toLowerCase().includes("topup") || h.kind === "issue";
        return (
          <div key={i} className="majhe-hist-row" data-testid="hist-row">
            <div className="majhe-hist-row__icon">
              {isCredit ? <CardIcon size={22} weight="fill"/> : <CurrencyInr size={22} weight="bold"/>}
            </div>
            <div>
              <div className="majhe-hist-row__title">{h.kind || "Transit Card Top Up"}</div>
              <div className="majhe-hist-row__sub">
                {isCredit ? <ArrowUpRight size={14} className="text-[#2D1170]"/> : <ArrowDownLeft size={14} className="text-[#7B5BF0]"/>}
                Credit by {h.by || "User"}
              </div>
              <div className="majhe-hist-row__date">{new Date(h.at).toLocaleString()}</div>
            </div>
            <div className={`majhe-hist-row__amount ${!isCredit ? "debit" : ""}`}>
              ₹ {Number(h.amount).toFixed(2)}
            </div>
          </div>
        );
      })}
    </Sheet>
  );
}

function RechargeSheet({ mode, activeCard, user, onClose, onDone }) {
  // mode = card_no (existing) | 'new' (recharge any card by number) | 'apply' (issue brand new)
  const [holder, setHolder] = useState(user?.name || "");
  const [cardNo, setCardNo] = useState(activeCard?.card_no || "");
  const [mobile] = useState(user?.mobile || "");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const title = mode === "apply" ? "Apply for New Card"
    : mode === "new" ? "Recharge New Card" : "Recharge";

  const submit = async () => {
    if (mode === "apply") {
      const initial = Number(amount || 100);
      if (initial < 100 || initial > 10000) {
        toast.error("Initial balance must be between ₹100 and ₹10,000"); return;
      }
      setBusy(true);
      try {
        const { data } = await passengerApi.post("/cards/apply", { initial_balance: initial, holder_name: holder });
        toast.success(`Card issued · ${data.card_no}`);
        onDone();
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
      finally { setBusy(false); }
      return;
    }
    if (!cardNo.trim()) { toast.error("Enter card number"); return; }
    const amt = Number(amount);
    if (amt < 50 || amt > 10000) { toast.error("Recharge between ₹50 - ₹10,000"); return; }
    setBusy(true);
    try {
      const { data } = await passengerApi.post("/cards/recharge", { card_no: cardNo, amount: amt });
      toast.success(`+₹${data.amount_added} · New balance ₹${data.new_balance}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Sheet title={title} onClose={onClose} tid="recharge-sheet">
      <div className="space-y-4">
        {mode === "apply" && (
          <FieldShell label="Full Name" icon={<UserIcon/>}>
            <input className="majhe-input" data-testid="apply-holder"
              placeholder="Your name" value={holder} onChange={(e) => setHolder(e.target.value)}/>
          </FieldShell>
        )}
        <FieldShell label="Mobile Number" icon={<Phone size={18} weight="bold"/>}>
          <div className="flex items-center gap-2">
            <span className="text-[#5B2EE5] font-bold pl-12">+91</span>
            <input className="majhe-input" style={{ paddingLeft: 16 }}
              value={mobile} readOnly data-testid="recharge-mobile"/>
          </div>
        </FieldShell>
        {mode !== "apply" && (
          <FieldShell label="Your Card Number" icon={<CardIcon size={18} weight="bold"/>}>
            <input className="majhe-input" data-testid="recharge-cardno"
              placeholder="GN2505XXXXXXX or card no"
              value={cardNo} onChange={(e) => setCardNo(e.target.value)}/>
          </FieldShell>
        )}
        <FieldShell label={mode === "apply" ? "Initial Top-up" : "Amount"} icon={<CurrencyInr size={18} weight="bold"/>}>
          <input className="majhe-input" inputMode="numeric" data-testid="recharge-amount"
            placeholder={mode === "apply" ? "100" : "Amount"}
            value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}/>
        </FieldShell>
        <div className="grid grid-cols-4 gap-1.5">
          {[100, 200, 500, 1000].map((a) => (
            <button key={a} onClick={() => setAmount(String(a))} type="button"
              data-testid={`amt-quick-${a}`}
              className={`py-2 rounded-lg text-xs font-bold transition-colors ${
                String(amount) === String(a) ? "bg-[#5B2EE5] text-white" : "bg-[#F5F2FF] text-[#5B2EE5]"
              }`}>
              ₹{a}
            </button>
          ))}
        </div>
      </div>
      <div className="majhe-sticky-cta" style={{ position: "static", margin: "24px 0 0" }}>
        <button data-testid="recharge-submit" onClick={submit} disabled={busy}
          className="majhe-btn">
          {busy ? "Processing…" : (mode === "apply" ? "Apply & Pay" : "Recharge")}
        </button>
      </div>
    </Sheet>
  );
}

function LinkCardSheet({ onClose, onDone }) {
  const [cardNo, setCardNo] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!cardNo.trim()) { toast.error("Enter card number"); return; }
    setBusy(true);
    try {
      const { data } = await passengerApi.post("/cards/link", { card_no: cardNo });
      toast.success(`Linked · ${data.card_no} · ₹${data.balance}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link this card");
    } finally { setBusy(false); }
  };
  return (
    <Sheet title="Link Physical Card" onClose={onClose} tid="link-sheet">
      <div className="text-sm text-[#7B7390] mb-4">
        Enter the card number printed on your physical KTCL transit card. You can link unlimited cards.
      </div>
      <FieldShell label="Card Number" icon={<CardIcon size={18} weight="bold"/>}>
        <input className="majhe-input" data-testid="link-cardno"
          placeholder="GN2505XXXXXXX" autoCapitalize="characters"
          value={cardNo} onChange={(e) => setCardNo(e.target.value.toUpperCase())}/>
      </FieldShell>
      <button onClick={submit} disabled={busy} data-testid="link-submit"
        className="majhe-btn mt-5">{busy ? "Linking…" : "Link Card"}</button>
    </Sheet>
  );
}

export function FieldShell({ label, icon, children }) {
  return (
    <div className="majhe-field">
      <div className="majhe-field__label">{label}</div>
      {icon && <div className="majhe-field__icon">{icon}</div>}
      {children}
    </div>
  );
}

function Sheet({ title, onClose, tid, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end" style={{ background: "rgba(15,8,40,0.55)" }}
      onClick={onClose} data-testid={tid}
      role="dialog" aria-modal="true" aria-labelledby={`${tid}-title`}>
      <div className="bg-white w-full rounded-t-[28px] overflow-hidden" style={{ maxHeight: "92dvh" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#F0EBFC] flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 id={`${tid}-title`} className="text-lg font-extrabold text-[#18142A]">{title}</h2>
          <button onClick={onClose} type="button" aria-label={`Close ${title.toLowerCase()}`}
            className="text-[#7B7390] font-bold text-2xl leading-none px-2"><X size={22} aria-hidden="true"/></button>
        </div>
        <div className="overflow-y-auto p-5 majhe-sheet-body" style={{ maxHeight: "75dvh" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function ApplyCardSheet({ user, onClose, onDone }) {
  const { meta } = useMajheMeta();
  const cardCategories = meta?.transit_card_categories || ["General Public"];
  const depots = meta?.depots || ["Panaji"];
  const docRequirements = meta?.card_doc_requirements || {};
  const cardFee = Number(meta?.card_fee ?? 150);
  const depositEnabled = !!meta?.card_deposit_enabled;
  const depositAmount = Number(meta?.card_deposit_amount ?? 0);

  const [category, setCategory] = useState("General Public");
  const [depot, setDepot] = useState("Panaji");
  const [holder, setHolder] = useState(user?.name || "");
  const [amount, setAmount] = useState("100");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(false);

  const requiredDocs = docRequirements[category] || [];
  const setDoc = (key, val) => setDocs((d) => ({ ...d, [key]: val }));

  const submit = async () => {
    const initial = Number(amount || 100);
    if (!holder.trim()) { toast.error("Enter full name"); return; }
    if (initial < 100 || initial > 10000) {
      toast.error("Initial balance must be between ₹100 and ₹10,000"); return;
    }
    const missing = requiredDocs
      .filter((d) => d.required && !(docs[d.key] || "").trim())
      .map((d) => d.label);
    if (missing.length) { toast.error(`Please upload: ${missing.join(", ")}`); return; }

    setBusy(true);
    try {
      const { data } = await passengerApi.post("/cards/apply", {
        initial_balance: initial, holder_name: holder,
        category, depot, address, city, pincode,
        institution_name: institutionName, documents: docs,
      });
      const hasDocs = Object.keys(docs).length > 0;
      toast.success(hasDocs
        ? `Card application ${data.card_no} submitted · track status anytime`
        : `Card issued · ${data.card_no}`);
      onDone({ navigateTo: hasDocs ? "/majhe/applications" : null });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Sheet title="Apply for Smart Transit Card" onClose={onClose} tid="apply-card-sheet">
      <div className="space-y-4">
        {/* STEP 1: pick the collection depot FIRST — before payment */}
        <div className="rounded-2xl bg-gradient-to-br from-[#6F2EE8] to-[#2D1170] text-white p-4 shadow-md" data-testid="collection-depot-block">
          <div className="text-[11px] uppercase tracking-wider font-bold opacity-90 flex items-center gap-1.5">
            <Buildings size={13} weight="bold"/> Step 1 · Collection Depot
          </div>
          <div className="text-[13px] mt-1.5 opacity-95">
            Choose where you want to pick up your physical Smart Transit Card. KTCL will keep it ready at the counter after documents are verified.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {depots.map((d) => (
              <button key={d} type="button" data-testid={`apply-card-depot-${d}`}
                onClick={() => setDepot(d)}
                className={`px-3 py-2 rounded-xl text-[12.5px] font-bold border transition-all ${
                  depot === d
                    ? "bg-white text-[#2D1170] border-white shadow-md"
                    : "bg-white/10 text-white border-white/30 hover:bg-white/20"
                }`}>
                {d} KTC
              </button>
            ))}
          </div>
        </div>

        <FieldShell label="Full Name">
          <input className="majhe-input" data-testid="apply-card-name" style={{ paddingLeft: 18 }}
            placeholder="Name as per Aadhar"
            value={holder} onChange={(e) => setHolder(e.target.value)}/>
        </FieldShell>

        <div className="grid grid-cols-2 gap-3">
          <FieldShell label="Category">
            <select className="majhe-input" data-testid="apply-card-cat" style={{ paddingLeft: 14, appearance: "auto" }}
              value={category} onChange={(e) => { setCategory(e.target.value); setDocs({}); }}>
              {cardCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FieldShell>
          <FieldShell label="Initial Top-up (₹)">
            <input className="majhe-input" inputMode="numeric" data-testid="apply-card-amount" style={{ paddingLeft: 18 }}
              placeholder="100" value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}/>
          </FieldShell>
        </div>

        {category === "Student" && (
          <FieldShell label="Institution / College Name" icon={<Buildings size={18} weight="bold"/>}>
            <input className="majhe-input" data-testid="apply-card-institution"
              placeholder="Goa Engineering College"
              value={institutionName} onChange={(e) => setInstitutionName(e.target.value)}/>
          </FieldShell>
        )}

        <FieldShell label="Address" icon={<MapPin size={18} weight="bold"/>}>
          <input className="majhe-input" data-testid="apply-card-addr"
            placeholder="House no, Street, Area"
            value={address} onChange={(e) => setAddress(e.target.value)}/>
        </FieldShell>
        <div className="grid grid-cols-2 gap-3">
          <FieldShell label="City / Town">
            <input className="majhe-input" data-testid="apply-card-city" style={{ paddingLeft: 18 }}
              placeholder="Panaji" value={city} onChange={(e) => setCity(e.target.value)}/>
          </FieldShell>
          <FieldShell label="PIN Code">
            <input className="majhe-input" data-testid="apply-card-pin" style={{ paddingLeft: 18 }}
              placeholder="403001" inputMode="numeric" maxLength={6}
              value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}/>
          </FieldShell>
        </div>

        {requiredDocs.length > 0 && (
          <div className="pt-2">
            <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold mb-2 px-1">
              Documents Required ({category})
            </div>
            <div className="space-y-3">
              {requiredDocs.map((d) => (
                <DocumentUploader key={d.key}
                  label={d.label} required={d.required}
                  value={docs[d.key] || ""}
                  onChange={(v) => setDoc(d.key, v)}
                  tid={`apply-card-doc-${d.key}`}/>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl bg-[#F5F2FF] border border-[#DCC9FF] text-[#4920C2] p-3 text-[12px]" data-testid="apply-card-charges">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 mb-2">Step 2 · Payment summary</div>
          <div className="flex items-center justify-between mb-1">
            <span>Registration Fee (credited back as balance)</span>
            <span className="font-bold">₹{cardFee}</span>
          </div>
          {depositEnabled && depositAmount > 0 && (
            <div className="flex items-center justify-between mb-1">
              <span>Refundable Deposit</span>
              <span className="font-bold text-amber-700">₹{depositAmount}</span>
            </div>
          )}
          <div className="flex items-center justify-between mb-1">
            <span>Initial Top-up</span>
            <span className="font-bold">₹{Number(amount || 100)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-[#DCC9FF] pt-2 mt-1">
            <span className="font-extrabold">Total to Pay</span>
            <span className="font-extrabold text-[#2D1170] text-base" data-testid="apply-card-total">
              ₹{cardFee + (depositEnabled ? depositAmount : 0) + Number(amount || 100)}
            </span>
          </div>
          <div className="text-[11px] mt-2 opacity-90">
            Collect from <b>{depot} KTC counter</b> after document verification.
            {depositEnabled && depositAmount > 0 && " Deposit refunded on card cancellation."}
          </div>
        </div>
      </div>
      <button onClick={submit} disabled={busy} data-testid="apply-card-submit"
        className="majhe-btn mt-5">
        {busy ? "Submitting…" : "Apply & Submit"}
      </button>
    </Sheet>
  );
}


function UserIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>;
}
